/**
 * TenantApexGuard — wraps routes that should only render on the
 * master apex (uyyamadilu.com), not on any tenant subdomain
 * (`<slug>.uyyamadilu.com`).
 *
 * Use case: every operator surface (Dashboard, IAM, Manage, Products,
 * Manufacturing, Warehouse, Order Management, Preferences, Customers)
 * and the customer build flow (/build, /build/:id). Tenant subdomains
 * are customer-facing storefronts only — landing here usually means
 * the visitor mistyped a URL or followed an off-context link.
 *
 * Behaviour:
 *   - On apex (tenant.isMaster) → renders children unchanged.
 *   - On a tenant subdomain → cross-origin hop to
 *     `https://uyyamadilu.com<same-path>`. Session cookie lives on
 *     `api.uyyamadilu.com` so it carries across the redirect for
 *     authed users.
 *   - While the tenant context is still loading (boot) → renders
 *     null so we don't flash operator content on a tenant subdomain
 *     before the redirect lands.
 *
 * Kiran 2026-06-07.
 */

import { useEffect, type ReactNode } from 'react'
import { useTenant } from '../context/TenantContext'

export function TenantApexGuard({ children }: { readonly children: ReactNode }): JSX.Element | null {
  const tenant = useTenant()

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (tenant.isLoading || tenant.isMaster) return
    const target = `https://uyyamadilu.com${window.location.pathname}${window.location.search}${window.location.hash}`
    window.location.replace(target)
  }, [tenant.isLoading, tenant.isMaster])

  if (tenant.isLoading) return null
  if (!tenant.isMaster) return null
  return <>{children}</>
}
